'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { NavMarker } from 'react-rhumb';
import UsageTracker from 'DesignManagerIde/utils/usageTracker/usageTracker';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIButton from 'UIComponents/button/UIButton';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { KEYS, MODIFIERS } from 'DesignData/enums/Keys';
import Keybinding from 'DesignData/models/Keybinding';
import { fetchOpenFolders, fetchRootFolderContents } from 'DesignData/actions/finder/Folders';
import { fetch as fetchOptionsAction } from 'DesignData/actions/finder/Options';
import { deselectAll as deselectAllAction } from 'DesignData/actions/finder/Selections';
import { getActiveAssetId } from 'DesignData/selectors/app';
import { makeGetFinderIsDragging } from 'DesignData/selectors/sharedDragDropContext';
import { getIsSearchFocusRequested, getRootFolderContentsFetchError, getIsFetchingRootFolderContents, getShowRecentlyEdited, getShowRecentlyDeleted } from 'DesignData/selectors/finder';
import { searchAssets, assetSearchSelectNextAsset, openSearchResultActiveAsset, openRecentlyEdited, openRecentlyDeleted } from 'DesignData/actions/finder/Assets';
import { FINDER_FILE, FINDER_FOLDER } from 'DesignData/enums/DragDropItemTypes';
import FinderDragLayer from 'DesignFinderUI/components/dnd/FinderDragLayer';
import SearchInput from 'DesignFinderUI/components/search/SearchInput';
import FinderActionsContainer from 'DesignFinderUI/components/actions/FinderActionsContainer';
import RootFolderNav from 'DesignFinderUI/components/nav/RootFolderNav';
import RecentlyDeletedContainer from 'DesignFinderUI/components/results/RecentlyDeletedContainer';
import RecentlyEditedContainer from 'DesignFinderUI/components/results/RecentlyEditedContainer';
import SearchContainer from 'DesignFinderUI/components/search/SearchContainer';
import CommandBar from 'DesignFinderUI/components/commandbar/CommandBar';
import withScrollDropTargets from 'DesignComponents/dnd/withScrollDropTargets';
import ExistingUserShowFinderStep from 'DesignFinderUI/components/onboarding/ExistingUserShowFinderStep';
import ExistingUserAfterNewFolderStep from 'DesignFinderUI/components/onboarding/ExistingUserAfterNewFolderStep';
import FinderWrapper from 'DesignFinderUI/containers/FinderWrapper';
const MINIMUM_SEARCH_COUNT = 1;
const finderDropTypes = [FINDER_FILE, FINDER_FOLDER];
const ScrollContainerWithDropTargets = withScrollDropTargets(finderDropTypes)(UIScrollContainer);

function makeMapStateToProps() {
  const getIsDragging = makeGetFinderIsDragging();
  return (state, ownProps) => {
    return {
      activeAssetId: getActiveAssetId(state),
      isDragging: getIsDragging(state, ownProps),
      isSearchFocusRequested: getIsSearchFocusRequested(state),
      fetchError: getRootFolderContentsFetchError(state),
      isFetchingContents: getIsFetchingRootFolderContents(state),
      showRecentlyEdited: getShowRecentlyEdited(state),
      showRecentlyDeleted: getShowRecentlyDeleted(state)
    };
  };
}

const mapDispatchToProps = {
  deselectAll: deselectAllAction,
  fetchOpenFolders,
  fetchOptions: fetchOptionsAction,
  fetchRootFolderContents,
  searchFiles: searchAssets,
  assetSearchSelectNextAsset,
  openSearchResultActiveAsset,
  showFinderRecentlyEdited: openRecentlyEdited,
  showFinderRecentlyDeleted: openRecentlyDeleted
};

class Finder extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      folderCanDrop: null,
      searchTerm: null
    };

    this.assetSearchSelectNextAsset = () => {
      this.props.assetSearchSelectNextAsset(false);
    };

    this.assetSearchSelectPreviousAsset = () => {
      this.props.assetSearchSelectNextAsset(true);
    };

    this.handleSearchAssetSelect = () => {
      this.handleSearchClose();
      this.props.openSearchResultActiveAsset();
    };

    this.keyConfig = {
      [MODIFIERS.NONE]: {
        [KEYS.UP_ARROW]: new Keybinding({
          handler: this.assetSearchSelectNextAsset
        }),
        [KEYS.DOWN_ARROW]: new Keybinding({
          handler: this.assetSearchSelectPreviousAsset
        }),
        [KEYS.ENTER]: new Keybinding({
          handler: this.handleSearchAssetSelect
        })
      }
    };

    this.getKeyConfig = () => {
      if (this.state.searchTerm) return this.keyConfig;
      return {};
    };

    this.handleSearchChange = __searchTerm => {
      const {
        searchFiles
      } = this.props;
      const searchTerm = __searchTerm && __searchTerm.length >= MINIMUM_SEARCH_COUNT ? __searchTerm : null;
      this.setState({
        searchTerm
      });
      if (searchTerm) searchFiles(searchTerm);
    };

    this.handleSearchClose = () => {
      this.setState({
        searchTerm: null
      });
    };

    this.handleUpdateFolderCanDrop = folderCanDrop => {
      this.setState({
        folderCanDrop
      });
    };

    this.closeContextMenu = () => {
      this.props.closeContextMenu();
    };

    this.handleClick = () => {
      const {
        deselectAll
      } = this.props;
      const dialogNodes = document.querySelectorAll('.uiDialog-root');

      if (dialogNodes.length) {
        return;
      }

      deselectAll();
    };
  }

  componentDidMount() {
    const {
      fetchOptions
    } = this.props;
    fetchOptions();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isDragging !== prevProps.isDragging) {
      this.setState({
        folderCanDrop: null
      });
    }
  }

  renderSearchInput() {
    const {
      isDragging,
      isSearchFocusRequested,
      showRecentlyDeleted,
      showRecentlyEdited
    } = this.props;
    const {
      searchTerm
    } = this.state;

    if (showRecentlyDeleted || showRecentlyEdited) {
      return null;
    }

    return /*#__PURE__*/_jsx("div", {
      className: "m-y-4 m-x-3",
      children: /*#__PURE__*/_jsx(SearchInput, {
        disabled: isDragging,
        initialSearchTerm: searchTerm,
        minimumSearchCount: MINIMUM_SEARCH_COUNT,
        onSearchChange: this.handleSearchChange,
        isFocusRequested: isSearchFocusRequested,
        keybindingConfig: this.getKeyConfig()
      })
    });
  }

  renderActionsDropdowns() {
    const {
      isDragging,
      showRecentlyDeleted,
      showRecentlyEdited
    } = this.props;
    const {
      searchTerm
    } = this.state;

    if (searchTerm || showRecentlyDeleted || showRecentlyEdited) {
      return null;
    }

    return /*#__PURE__*/_jsx("div", {
      className: "m-x-4",
      children: /*#__PURE__*/_jsx(FinderActionsContainer, {
        disabled: isDragging,
        onShowRecentlyDeleted: this.props.showFinderRecentlyDeleted,
        onShowRecentlyEdited: this.props.showFinderRecentlyEdited
      })
    });
  }

  renderContents() {
    const {
      showRecentlyDeleted,
      showRecentlyEdited
    } = this.props;
    const {
      searchTerm
    } = this.state;

    if (searchTerm) {
      return this.renderSearchContainer();
    }

    if (showRecentlyDeleted) {
      return this.renderRecentlyDeleted();
    }

    if (showRecentlyEdited) {
      return this.renderRecentlyEdited();
    }

    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(CommandBar, {
        onUpdateCanDrop: this.handleUpdateFolderCanDrop,
        closeContextMenu: this.props.closeContextMenu,
        openContextMenu: this.props.openContextMenu,
        usageTracker: UsageTracker
      }), /*#__PURE__*/_jsx(ScrollContainerWithDropTargets, {
        className: "finder-container__files",
        onClick: this.handleClick,
        onScroll: this.closeContextMenu,
        children: this.renderRootFolderNav()
      })]
    });
  }

  renderRootFolderNav() {
    if (this.props.isFetchingContents) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true,
        minHeight: 100
      });
    }

    if (this.props.fetchError) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "FINDER_ERROR",
        children: /*#__PURE__*/_jsxs(UIErrorMessage, {
          illustrationProps: {
            width: 0
          },
          title: unescapedText('DesignFinderUI.rootFolderNav.fetchError.title'),
          children: [/*#__PURE__*/_jsx("p", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "DesignFinderUI.rootFolderNav.fetchError.message"
            })
          }), /*#__PURE__*/_jsx("p", {
            children: /*#__PURE__*/_jsxs(UIButton, {
              onClick: this.props.fetchRootFolderContents,
              use: "link",
              children: [/*#__PURE__*/_jsx(UIIcon, {
                name: "refresh"
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "DesignFinderUI.rootFolderNav.fetchError.retryButtonText"
              })]
            })
          })]
        })
      });
    }

    return /*#__PURE__*/_jsx(NavMarker, {
      name: "FINDER",
      children: /*#__PURE__*/_jsx(RootFolderNav, {
        onUpdateCanDrop: this.handleUpdateFolderCanDrop,
        closeContextMenu: this.props.closeContextMenu,
        openContextMenu: this.props.openContextMenu
      })
    });
  }

  renderRecentlyDeleted() {
    if (!this.props.showRecentlyDeleted) {
      return null;
    }

    return /*#__PURE__*/_jsx(RecentlyDeletedContainer, {});
  }

  renderRecentlyEdited() {
    if (!this.props.showRecentlyEdited) {
      return null;
    }

    return /*#__PURE__*/_jsx(RecentlyEditedContainer, {});
  }

  renderSearchContainer() {
    const {
      searchTerm
    } = this.state;

    if (!searchTerm) {
      return null;
    }

    return /*#__PURE__*/_jsx(SearchContainer, {
      onClose: this.handleSearchClose,
      searchTerm: searchTerm
    });
  }

  render() {
    const {
      folderCanDrop
    } = this.state;
    const {
      children,
      isDragging
    } = this.props;
    const finderClasses = isDragging ? 'is--dragging' : "";
    const renderedSearchInput = this.renderSearchInput();
    const renderedActionsDropdowns = this.renderActionsDropdowns();
    const renderedContents = this.renderContents();
    return /*#__PURE__*/_jsx(ExistingUserAfterNewFolderStep, {
      children: /*#__PURE__*/_jsx(ExistingUserShowFinderStep, {
        children: /*#__PURE__*/_jsxs(FinderWrapper, {
          className: finderClasses,
          children: [children, /*#__PURE__*/_jsx(FinderDragLayer, {
            canDrop: folderCanDrop,
            isDragging: isDragging
          }), /*#__PURE__*/_jsxs("div", {
            className: "finder-container__actions",
            children: [renderedSearchInput, renderedActionsDropdowns]
          }), renderedContents]
        })
      })
    });
  }

}

Finder.propTypes = {
  children: PropTypes.node,
  deselectAll: PropTypes.func.isRequired,
  fetchError: PropTypes.instanceOf(Error),
  fetchOptions: PropTypes.func.isRequired,
  fetchRootFolderContents: PropTypes.func.isRequired,
  searchFiles: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  closeContextMenu: PropTypes.func.isRequired,
  openContextMenu: PropTypes.func.isRequired,
  isSearchFocusRequested: PropTypes.bool,
  isFetchingContents: PropTypes.bool.isRequired,
  assetSearchSelectNextAsset: PropTypes.func.isRequired,
  openSearchResultActiveAsset: PropTypes.func.isRequired,
  showRecentlyEdited: PropTypes.bool.isRequired,
  showRecentlyDeleted: PropTypes.bool.isRequired,
  showFinderRecentlyEdited: PropTypes.func.isRequired,
  showFinderRecentlyDeleted: PropTypes.func.isRequired
};
export default connect(makeMapStateToProps, mapDispatchToProps)(Finder);